import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import ShareModal from "./models/sharing";
import { CountryModel } from "../common/countryModel";
import { ensureChatSocket, subscribeContactSync } from "../../services/chatSocketManager";
import {
    applyContactListPatch,
    CONTACTS_CHANGED_EVENT,
    dispatchContactsChanged,
    extractContactFromSocketPayload,
    mergeContactRecord,
} from "../../utils/contactListSync";

// 🔹 Reusable Modal Component
export function ModalWrapper({ children, onClose, className = "" }) {
    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm`}
            onClick={onClose}
        >
            <div
                className={`bg-white rounded-2xl shadow-xl p-6 w-[320px] ${className}`}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
}

export default function ChatBar({
    contacts = [],
    groupList = [],
    bdayData = [],
    friendResData = [],
    onSelectContact,
    chatScreen = false,
    selectedContactId = null,
    selectedGroupId = null,
    redirectToChat,
    filterSaw = true,
    loading = false,
    chatListLoaded = true,
    onRefreshContacts, // optional: parent can pass a refresh function
    externalInviteTrigger, // new prop: function to trigger invite modal externally
    disableAutoRefresh = false,
    onSelectGroupDetails, // new prop: for opening group details modal
    onGroupInfoClick,    // 🔹 NEW: for showing group details info
    onGroupUnmoved,
    prioritizeUserId = null,
}) {

    const token = Cookies.get("auth_token");

    // 🔹 Live contact refresh: poll + focus (friend accept/remove is API-driven, not socket)
    useEffect(() => {
        if (chatScreen || disableAutoRefresh) return;
        if (!token) return;

        const refreshContacts = () => {
            if (typeof onRefreshContacts === "function") {
                onRefreshContacts();
            } else {
                setRefreshing((r) => !r);
            }
        };

        const pollInterval = setInterval(() => {
            if (!document.hidden) {
                refreshContacts();
            }
        }, 10000);

        const handleFocus = () => refreshContacts();
        const handleVisible = () => {
            if (!document.hidden) refreshContacts();
        };

        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisible);

        return () => {
            clearInterval(pollInterval);
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener("visibilitychange", handleVisible);
        };
    }, [chatScreen, disableAutoRefresh, token, onRefreshContacts]);
    const navigate = useNavigate();
    const location = useLocation();

    const [activeFilters, setActiveFilters] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [confirmModal, setConfirmModal] = useState(false);
    const [tagModal, setTagModal] = useState(false);
    const [shareLink, setShareLink] = useState("");
    const [shareModal, setShareModal] = useState(false);
    const [friendRequestModal, setFriendRequestModal] = useState(false);
    const [friendRequestTarget, setFriendRequestTarget] = useState(null);
    const [inviteNumber, setInviteNumber] = useState("");
    const [showAddContactModal, setShowAddContactModal] = useState(false);
    const [showCountryModal, setShowCountryModal] = useState(false);
    const [submittingContact, setSubmittingContact] = useState(false);
    const [contactFormTouched, setContactFormTouched] = useState({ phone: false });
    const [contactForm, setContactForm] = useState({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        countryCode: "+91",
    });
    const [localContacts, setLocalContacts] = useState(contacts);

    // 🔹 Separate tags state for different modals to prevent pollution
    const [tagModalTags, setTagModalTags] = useState([]);
    const [friendRequestTags, setFriendRequestTags] = useState([]);

    const getStatusDataSignature = (statusData) => {
        if (!Array.isArray(statusData)) return "";
        return statusData
            .map((entry) =>
                [
                    entry?.offlineuntil,
                    entry?.is_expired,
                    entry?.until,
                    entry?.date,
                    entry?.time,
                ].join("~")
            )
            .join("|");
    };

    const getContactSignature = (contact) => {
        if (!contact) return "";
        return [
            contact?.user_id,
            contact?.group_id,
            contact?.last_message,
            contact?.last_message_time,
            contact?.group_message_read,
            contact?.unread_count,
            contact?.message_type,
            contact?.is_friend,
            contact?.phone_book_user,
            contact?.waitlist_status,
            contact?.pending_relation_selection,
            contact?.invite_link_case,
            contact?.request_send,
            contact?.relations,
            contact?.active_relations,
            contact?.status_text,
            getStatusDataSignature(contact?.status_data),
            contact?.profile_image || contact?.profile_image_url,
            contact?.nick_name,
            contact?.full_name,
            contact?.group_name,
            contact?.group_image_url,
        ].join("::");
    };

    const getContactKey = (contact) =>
        String(contact?.user_id || contact?.group_id || "");

    const getContactsOrderKey = (list) =>
        (Array.isArray(list) ? list : []).map((contact) => getContactKey(contact)).join("|");

    const mergeContactWithOptimisticState = mergeContactRecord;

    const hasUnreadMessages = (contact, activeId) =>
        Number(contact?.unread_count) > 0 &&
        String(contact?.user_id) !== String(activeId);

    // Keep localContacts in sync with incoming props so list updates when API returns
    useEffect(() => {
        const nextContacts = Array.isArray(contacts) ? contacts : [];
        const activeId = chatScreen ? selectedContactId : null;

        setLocalContacts((prev) => {
            const previousByKey = new Map(prev.map((contact) => [getContactKey(contact), contact]));
            const merged = nextContacts.map((contact) => {
                const mergedContact = mergeContactWithOptimisticState(
                    previousByKey.get(getContactKey(contact)),
                    contact
                );
                if (
                    activeId &&
                    String(mergedContact.user_id) === String(activeId) &&
                    Number(mergedContact.unread_count) > 0
                ) {
                    return { ...mergedContact, unread_count: 0 };
                }
                return mergedContact;
            });

            const orderChanged = getContactsOrderKey(prev) !== getContactsOrderKey(merged);
            const dataChanged = merged.some((contact) => {
                const key = getContactKey(contact);
                const previous = previousByKey.get(key);
                if (!previous) return true;
                return getContactSignature(previous) !== getContactSignature(contact);
            }) || prev.length !== merged.length;

            if (!orderChanged && !dataChanged) {
                return prev;
            }

            return merged;
        });
    }, [contacts, chatScreen, selectedContactId]);

    // Live socket + global contact sync bus
    useEffect(() => {
        if (!token) return;

        ensureChatSocket(token);

        const activeId = chatScreen ? selectedContactId : null;

        const scheduleRefresh = () => {
            if (typeof onRefreshContacts !== "function") return;
            if (friendRefreshTimerRef.current) clearTimeout(friendRefreshTimerRef.current);
            friendRefreshTimerRef.current = setTimeout(() => {
                Promise.resolve(onRefreshContacts());
                friendRefreshTimerRef.current = null;
            }, 300);
        };

        const socket = ensureChatSocket(token);
        if (!socket) return;

        const onChatListUpdated = (payload) => {
            const conversation = extractContactFromSocketPayload(payload);
            if (!conversation?.user_id) return;

            const userId = conversation.user_id;
            const unreadCount = conversation.unread_count ?? 0;
            const clearedUnread = String(userId) === String(activeId) ? 0 : unreadCount;

            const patch = {
                ...conversation,
                last_message: conversation.last_message ?? "",
                last_message_time: conversation.last_message_time || new Date().toISOString(),
                message_type: conversation.message_type || "text",
                unread_count: clearedUnread,
            };

            setLocalContacts((prev) => applyContactListPatch(prev, patch, { moveToTop: true }));
        };

        const onGroupListUpdated = (payload) => {
            const group = payload?.group || payload?.data?.group || payload;
            if (!group?.group_id) return;

            setLocalContacts((prev) => {
                const existingIndex = prev.findIndex((c) => String(c.group_id) === String(group.group_id));
                const lastMessageTime =
                    group.last_message_time ||
                    group.updated_at ||
                    group.created_at ||
                    new Date().toISOString();

                const updatedGroup = existingIndex >= 0
                    ? {
                        ...prev[existingIndex],
                        ...group,
                        last_message_time: lastMessageTime,
                    }
                    : {
                        ...group,
                        last_message_time: lastMessageTime,
                    };

                if (existingIndex >= 0) {
                    return [updatedGroup, ...prev.filter((_, index) => index !== existingIndex)];
                }
                return [updatedGroup, ...prev];
            });
        };

        const onGlobalContactSync = ({ type, payload }) => {
            if (type === "socket" && payload) {
                const contact = extractContactFromSocketPayload(payload);
                if (contact?.user_id) {
                    setLocalContacts((prev) => applyContactListPatch(prev, contact));
                }
            }
            if (type === "socket" || type === "refresh") {
                scheduleRefresh();
            }
        };

        const onContactsChanged = () => scheduleRefresh();

        socket.on("chat_list_updated", onChatListUpdated);
        socket.on("group_list_updated", onGroupListUpdated);
        const unsubscribe = subscribeContactSync(onGlobalContactSync);
        window.addEventListener(CONTACTS_CHANGED_EVENT, onContactsChanged);

        return () => {
            socket.off("chat_list_updated", onChatListUpdated);
            socket.off("group_list_updated", onGroupListUpdated);
            unsubscribe();
            window.removeEventListener(CONTACTS_CHANGED_EVENT, onContactsChanged);
            if (friendRefreshTimerRef.current) {
                clearTimeout(friendRefreshTimerRef.current);
                friendRefreshTimerRef.current = null;
            }
        };
    }, [token, chatScreen, selectedContactId, onRefreshContacts]);

    // Clear unread badge/icon for the chat currently open
    useEffect(() => {
        if (!chatScreen || !selectedContactId) return;

        setLocalContacts((prev) => {
            let changed = false;
            const next = prev.map((contact) => {
                if (String(contact.user_id) !== String(selectedContactId)) return contact;
                if (Number(contact.unread_count) <= 0) return contact;
                changed = true;
                return { ...contact, unread_count: 0 };
            });
            return changed ? next : prev;
        });
    }, [chatScreen, selectedContactId]);

    // 🔹 Group-specific states for unmove functionality
    const [groupImagePreview, setGroupImagePreview] = useState(null);
    const [showGroupImageModal, setShowGroupImageModal] = useState(false);
    const [movingGroupId, setMovingGroupId] = useState(null);

    // useEffect(() => {
    //     setLocalContacts(contacts);
    // }, [contacts]);

    const [searchRaw, setSearchRaw] = useState(""); // 🔹 Raw input
    const [search, setSearch] = useState(""); // 🔹 Debounced search
    const [profileData, setProfileData] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [sortOrder, setSortOrder] = useState("default"); // default, a-z, z-a, oldest, unread
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [statusAutoScrollDone, setStatusAutoScrollDone] = useState({});
    const [imagePreview, setImagePreview] = useState(null); // 🔹 For image modal preview
    const [showImageModal, setShowImageModal] = useState(false); // 🔹 Image modal visibility
    const sortDropdownRef = useRef(null);
    const abortControllerRef = useRef(null); // 🔹 For cancelling API requests
    const searchTimeoutRef = useRef(null); // 🔹 For debouncing search
    const friendRefreshTimerRef = useRef(null);

    const toggleTagSelection = (tag, setTags) => {
        setTags((prevTags) => {
            if (tag === "Other") {
                return prevTags.includes("Other") ? [] : ["Other"];
            }

            const nextTags = prevTags.filter((value) => value !== "Other");
            return nextTags.includes(tag)
                ? nextTags.filter((value) => value !== tag)
                : [...nextTags, tag];
        });
    };

    // 🔹 Search debouncing (300ms delay to prevent excessive filtering)
    useEffect(() => {
        searchTimeoutRef.current = setTimeout(() => {
            setSearch(searchRaw);
        }, 300);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
        };
    }, [searchRaw]);

    // UI-only special filters (used to suppress 'Default' sort highlight)
    const UI_SPECIAL_FILTERS = ["Unadded phonebookplus contacts", "Uninvited contacts"];

    const markStatusAutoDone = (userId) => {
        const key = String(userId || "");
        if (!key) return;
        setStatusAutoScrollDone((prev) => {
            if (prev[key]) return prev;
            return { ...prev, [key]: true };
        });
    };

    const handleStatusWheel = (event, userId) => {
        markStatusAutoDone(userId);
        const container = event.currentTarget;
        if (!container) return;
        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
            container.scrollLeft += event.deltaY;
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const parseContactStatusText = (raw) => {
        if (raw === null || raw === undefined) return "";
        if (typeof raw === "string") return raw;
        if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
        if (typeof raw === "object") {
            if (raw.ciphertext && raw.nonce) return "[Encrypted status]";
            try {
                return JSON.stringify(raw);
            } catch {
                return "";
            }
        }
        return "";
    };

    const formatOfflineStatusMessage = (offlineStatus) => {
        if (!offlineStatus) return "";
        let iso =
            offlineStatus?.until ||
            (offlineStatus?.date && offlineStatus?.time
                ? `${offlineStatus.date}T${offlineStatus.time}Z`
                : null);
        if (!iso && offlineStatus?.date) iso = offlineStatus.date;

        const dt = iso ? new Date(iso) : null;
        if (dt && !isNaN(dt)) {
            const userLocale =
                typeof navigator !== "undefined" && navigator.language
                    ? navigator.language
                    : "en-US";
            const userTimeZone =
                typeof Intl !== "undefined" && Intl.DateTimeFormat
                    ? Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
                    : undefined;
            const dateOpts = {
                day: "2-digit",
                month: "short",
                year: "numeric",
                ...(userTimeZone ? { timeZone: userTimeZone } : {}),
            };
            const timeOpts = {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                ...(userTimeZone ? { timeZone: userTimeZone } : {}),
            };
            const datePart = new Intl.DateTimeFormat(userLocale, dateOpts)
                .format(dt)
                .toUpperCase();
            const timePart = new Intl.DateTimeFormat(userLocale, timeOpts).format(dt);
            return `Offline Until ${datePart}, ${timePart}. Delivery and responses may be delayed`;
        }
        return "Offline. Delivery and responses may be delayed";
    };

    const isFriendContact = (value) =>
        value === 1 || value === "1" || value === true || value === "true";

    const isAdminContact = (contact) =>
        /^admin/i.test(String(contact?.user_id || ""));

    const getContactPhone = (contact) =>
        contact?.phone || contact?.phone_number || contact?.mobile || contact?.personal_mobile || "";

    const openFriendProfile = useCallback((userId) => {
        if (!userId) return;
        navigate(`/friend-profile/${userId}`, {
            state: { from: location?.pathname || null },
        });
    }, [location?.pathname, navigate]);

    const shouldOpenFriendProfile = useCallback((contact) => {
        return !chatScreen && isFriendContact(contact?.is_friend) && !isAdminContact(contact);
    }, [chatScreen]);

    const openContactFromList = useCallback((contact) => {
        if (!contact) return;

        if (chatScreen) {
            onSelectContact?.(contact.user_id, getContactPhone(contact));
            return;
        }

        if (shouldOpenFriendProfile(contact)) {
            openFriendProfile(contact.user_id);
            return;
        }

        onSelectContact?.(contact);
    }, [chatScreen, onSelectContact, openFriendProfile, shouldOpenFriendProfile]);

    const resetContactForm = useCallback(() => {
        setContactForm((prev) => ({
            firstName: "",
            lastName: "",
            phoneNumber: "",
            email: "",
            countryCode: prev.countryCode || "+91",
        }));
        setContactFormTouched({ phone: false });
    }, []);

    const handleBirthdayContactOpen = useCallback((entry) => {
        const userId = entry?.user_id || entry?.original?.user_id || entry?.original?.friend_user_id || entry?.original?.id;
        if (!userId) {
            toast.error("Unable to open this contact");
            return;
        }

        openFriendProfile(userId);
    }, [openFriendProfile]);

    const handleOpenAddContactModal = useCallback(() => {
        resetContactForm();
        setShowAddContactModal(true);
    }, [resetContactForm]);

    const handleCloseAddContactModal = useCallback(() => {
        setShowAddContactModal(false);
    }, []);

    const handleCountrySelect = (country) => {
        const dialCode = country?.dialCode ? `+${country.dialCode}` : "+91";
        setContactForm((prev) => ({ ...prev, countryCode: dialCode }));
        setShowCountryModal(false);
    };

    const validateAndBuildContactPayload = () => {
        const firstName = contactForm.firstName.trim();
        const lastName = contactForm.lastName.trim();
        const email = contactForm.email.trim();
        const countryCode = (contactForm.countryCode || "").trim();
        const phoneDigits = contactForm.phoneNumber.replace(/\D/g, "");

        if (!firstName) throw new Error("First name is required");
        if (!countryCode || !countryCode.startsWith("+")) throw new Error("Please select a valid country code");
        if (!phoneDigits) throw new Error("Phone number is required");
        if (countryCode === "+91") {
            if (phoneDigits.length !== 10) throw new Error("India number must be exactly 10 digits");
            if (!/^[1-9]\d{9}$/.test(phoneDigits)) throw new Error("Enter a valid India mobile number");
        } else {
            if (phoneDigits.length < 6 || phoneDigits.length > 15) throw new Error("Enter a valid phone number");
        }

        if (email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) throw new Error("Enter a valid email address");
        }

        const phoneForApi = countryCode === "+91" ? phoneDigits : `${countryCode}${phoneDigits}`;

        return {
            contacts: [
                {
                    first_name: firstName,
                    last_name: lastName,
                    phone_number: phoneForApi,
                    email,
                },
            ],
        };
    };

    const phoneDigits = contactForm.phoneNumber.replace(/\D/g, "");
    const isIndiaCode = (contactForm.countryCode || "").trim() === "+91";
    const phoneError = (() => {
        if (!phoneDigits) return "Phone number is required";
        if (isIndiaCode) {
            if (phoneDigits.length !== 10) return "India number must be exactly 10 digits";
            if (!/^[1-9]\d{9}$/.test(phoneDigits)) return "Enter a valid India mobile number";
            return "";
        }
        if (phoneDigits.length < 6 || phoneDigits.length > 15) return "Enter a valid phone number";
        return "";
    })();

    const emailError = (() => {
        const email = contactForm.email.trim();
        if (!email) return "";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email) ? "" : "Enter a valid email address";
    })();

    const disableSubmitContact =
        submittingContact ||
        !contactForm.firstName.trim() ||
        !!phoneError ||
        !!emailError;

    const handleSubmitContact = useCallback(async () => {
        try {
            if (!token) {
                toast.error("Session expired. Please login again.");
                navigate("/login");
                return;
            }

            if (phoneError) {
                setContactFormTouched((prev) => ({ ...prev, phone: true }));
                return;
            }

            const payload = validateAndBuildContactPayload();
            setSubmittingContact(true);

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/upload-contacts`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            const data = await res.json();
            if (!res.ok || data?.status === "0") {
                throw new Error(data?.message || "Failed to add contact");
            }

            if (typeof onRefreshContacts === "function") {
                await Promise.resolve(onRefreshContacts());
            }
            toast.success(data?.message || "Contact added successfully");
            setShowAddContactModal(false);
        } catch (err) {
            if (/(phone|india number|mobile number)/i.test(err?.message || "")) {
                setContactFormTouched((prev) => ({ ...prev, phone: true }));
                return;
            }
            toast.error(err?.message || "Failed to add contact");
        } finally {
            setSubmittingContact(false);
        }
    }, [token, phoneError, validateAndBuildContactPayload, onRefreshContacts, navigate]);

    const filters = ["Family", "Friend", "Work", "Other"];
    const inviteFilters = ["Family", "Friend", "Work", "Other"];

    // 🔹 Fetch profile data on component mount with cancellation support
    useEffect(() => {
        if (!token) return;

        const abortController = new AbortController();

        const fetchProfile = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    signal: abortController.signal
                });
                const data = await res.json();
                if (data.status === "1" && data.data?.[0]?.profile) {
                    setProfileData(data.data[0].profile);
                }
            } catch (error) {
                // Ignore abort errors
                if (error.name !== "AbortError") {
                    }
            }
        };

        fetchProfile();

        return () => abortController.abort();
    }, [token]);

    // 🔹 Handle refresh status API call with cancellation support
    const handleRefreshClick = useCallback(async () => {
        if (!token) {
            toast.error("Session expired. Please login again.");
            return;
        }

        // Cancel previous request if any
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setRefreshing(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/status/refresh`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`
                },
                signal: abortControllerRef.current.signal
            });
            const data = await res.json();
            if (data.status === "1") {
                toast.success("Status refreshed successfully!");
                // Refetch profile data after refresh
                try {
                    const profileRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`
                        },
                        signal: abortControllerRef.current.signal
                    });
                    const profileData = await profileRes.json();
                    if (profileData.status === "1" && profileData.data?.[0]?.profile) {
                        setProfileData(profileData.data[0].profile);
                    }
                } catch (error) {
                    if (error.name !== "AbortError") {
                        }
                }
            } else {
                toast.error(data?.message || "Failed to refresh status");
            }
        } catch (error) {
            if (error.name !== "AbortError") {
                toast.error("Error refreshing status");
                }
        } finally {
            setTimeout(() => {
                setRefreshing(false);
            }, 1500);
        }
    }, [token]);

    // 🔹 Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
                setShowSortDropdown(false);
            }
        };

        if (showSortDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showSortDropdown]);

    // 🔹 Normalize status field check (consistent type coercion)
    const isWaitlisted = useCallback((contact) => {
        return Number(contact?.waitlist_status) === 1;
    }, []);

    // 🔹 Handle Filter Toggle with memoization
    const handleFilterToggle = useCallback((filter) => {
        setActiveFilters((prev) => {
            const specialFilters = ["Unadded phonebookplus contacts", "Uninvited contacts"];

            if (specialFilters.includes(filter)) {
                if (prev.includes(filter)) {
                    return prev.filter((f) => f !== filter);
                }
                return [filter];
            }

            if (filter === "Public" || filter === "Other") {
                return prev.includes(filter) ? [] : [filter];
            } else {
                let next = prev.filter((f) => f !== "Other" && f !== "Public");
                if (next.includes(filter)) {
                    next = next.filter((x) => x !== filter);
                } else {
                    next = [...next, filter];
                }
                return next;
            }
        });
    }, []);

    // 🔹 Handle Unread Toggle with memoization
    const handleUnreadToggle = useCallback(() => {
        if (sortOrder === "unread") {
            setSortOrder("default");
        } else {
            setSortOrder("unread");
            setActiveFilters([]);
        }
    }, [sortOrder]);

    const contactsForList = localContacts;

    // 🔹 Filter and Sort logic
    const filteredContacts = useMemo(() => {
        let list = [...contactsForList, ...groupList];  // 🔹 NEW: Include both contacts and groups
        const getLastMessageTime = (item) => {
            const raw = item?.last_message_time || item?.lastMessageTime || item?.updated_at || item?.created_at;
            const time = raw ? new Date(raw).getTime() : 0;
            return Number.isFinite(time) ? time : 0;
        };
        const isWaitlistContact = (item) =>
            !(item?.group_id || item?.group_name) && Number(item?.waitlist_status || 0) === 1;
        const isPrioritizedContact = (item) =>
            prioritizeUserId && !(item?.group_id || item?.group_name) && String(item.user_id) === String(prioritizeUserId);
        const sortByRecentMessage = (a, b) => getLastMessageTime(b) - getLastMessageTime(a);
        const hasSpecialPromotionFilter = activeFilters.some((filter) => UI_SPECIAL_FILTERS.includes(filter));

        // 🔍 Search logic
        if (search.trim()) {
            const s = search.trim().toLowerCase();
            list = list.filter(
                (c) =>
                    c.full_name?.toLowerCase().includes(s) ||
                    c.first_name?.toLowerCase().includes(s) ||
                    c.email?.toLowerCase().includes(s) ||
                    c.group_name?.toLowerCase().includes(s) ||  // 🔹 NEW: Search by group name
                    c.phone?.toString().includes(s) ||
                    c.phone_number?.toString().includes(s)
            );
        }

        // 🔹 Filter by relation tags (matches any selected tag) - skip for groups
        // Note: 'Unadded phonebookplus contacts' and 'Uninvited contacts' are handled as promotions (sorting),
        // not as exclusive filters — see promotion block after sort logic.

        if (activeFilters.length === 1 && activeFilters[0] === "Others") {
            // On both chatScreen and contact page, 'Others' means 'Public'
            list = list.filter((c) => {
                // Skip filtering for groups
                if (c?.group_id || c?.group_name) return true;

                const raw = c.relations ?? c.active_relations ?? "";
                let relationList = [];
                if (Array.isArray(raw)) relationList = raw.map(r => String(r).trim());
                else if (raw && typeof raw === 'string') relationList = raw.split(',').map(s => s.trim()).filter(Boolean);
                else relationList = [];
                return relationList.includes("Public");
            });
        } else if (activeFilters.length && !activeFilters.includes("Public") && !activeFilters.includes("Others") && !activeFilters.includes("Unadded phonebookplus contacts") && !activeFilters.includes("Uninvited contacts")) {
            // For both chatScreen and !chatScreen, normal filter logic
            list = list.filter((c) => {
                // Skip filtering for groups
                if (c?.group_id || c?.group_name) return true;

                // Apply relation-tag filtering to all non-group entries (including non-phonebook users)

                // Normalize relations: support `relations` (array or csv) and `active_relations`
                const raw = c.relations ?? c.active_relations ?? "";
                let relationList = [];
                if (Array.isArray(raw)) relationList = raw.map(r => String(r).trim());
                else if (raw && typeof raw === 'string') relationList = raw.split(',').map(s => s.trim()).filter(Boolean);
                else relationList = [];

                return activeFilters.some((f) => relationList.includes(f));
            });
        }

        // 🔹 Sort logic - only apply to phone_book_user === 1 when NOT in chatScreen
        let phonebookContacts, nonPhonebookContacts;

        if (!chatScreen) {
            // Separate phonebook users from non-phonebook users
            phonebookContacts = list.filter(c => c.phone_book_user === 1);
            nonPhonebookContacts = list.filter(c => c.phone_book_user === 0);

            // Apply sorting only to phonebook contacts
            if (sortOrder === "a-z") {
                const getName = (x) => String(x?.full_name || x?.name || x?.first_name || x?.last_name || "").toLowerCase();
                phonebookContacts.sort((a, b) => getName(a).localeCompare(getName(b)));
            }
            else if (sortOrder === "z-a") {
                const getName = (x) => String(x?.full_name || x?.name || x?.first_name || x?.last_name || "").toLowerCase();
                phonebookContacts.sort((a, b) => getName(b).localeCompare(getName(a)));
            }
            else if (sortOrder === "oldest") {
                phonebookContacts.sort((a, b) => {
                    if (a.last_message_time && b.last_message_time) {
                        return new Date(a.last_message_time) - new Date(b.last_message_time);
                    }
                    if (a.last_message_time) return -1;
                    if (b.last_message_time) return 1;
                    return 0;
                });
            }
            else if (sortOrder === "unread") {
                phonebookContacts.sort((a, b) => {
                    const aUnread = Number(a.unread_count || 0);
                    const bUnread = Number(b.unread_count || 0);
                    return bUnread - aUnread; // Unread messages first (descending)
                });
            }

            // Combine: sorted phonebook contacts first, then non-phonebook contacts
            list = [...phonebookContacts, ...nonPhonebookContacts];
        } else {
            // In chatScreen mode: show all contacts without separating by phone_book_user
            if (sortOrder === "a-z") {
                const getName = (x) => String(x?.full_name || x?.name || x?.first_name || x?.last_name || "").toLowerCase();
                list.sort((a, b) => getName(a).localeCompare(getName(b)));
            }
            else if (sortOrder === "z-a") {
                const getName = (x) => String(x?.full_name || x?.name || x?.first_name || x?.last_name || "").toLowerCase();
                list.sort((a, b) => getName(b).localeCompare(getName(a)));
            }
            else if (sortOrder === "oldest") {
                list.sort((a, b) => {
                    if (a.last_message_time && b.last_message_time) {
                        return new Date(a.last_message_time) - new Date(b.last_message_time);
                    }
                    if (a.last_message_time) return -1;
                    if (b.last_message_time) return 1;
                    return 0;
                });
            }
            else if (sortOrder === "unread") {
                list.sort((a, b) => {
                    // For groups: check group_message_read (1 = unread, 0 = read)
                    // For contacts: check unread_count
                    const isGroupA = a?.group_id || a?.group_name;
                    const isGroupB = b?.group_id || b?.group_name;

                    const aUnread = isGroupA ? (a.group_message_read === 1 ? 1 : 0) : Number(a.unread_count || 0);
                    const bUnread = isGroupB ? (b.group_message_read === 1 ? 1 : 0) : Number(b.unread_count || 0);

                    return bUnread - aUnread; // Unread messages first (descending)
                });
            }
            // For "default", keep original order
        }

        // 🔹 Promote selected groups to top (don't remove others)
        const promotePredicates = [];
        const isPBButNotFriend = (c) => !(c?.group_id || c?.group_name) && Number(c.phoneBookUser ?? c.phone_book_user ?? 0) === 1 && Number(c.isFriend ?? c.is_friend ?? 0) === 0;
        const isNotPBUser = (c) => !(c?.group_id || c?.group_name) && Number(c.phoneBookUser ?? c.phone_book_user ?? 0) === 0;

        if (activeFilters.includes("Unadded phonebookplus contacts")) promotePredicates.push(isPBButNotFriend);
        if (activeFilters.includes("Uninvited contacts")) promotePredicates.push(isNotPBUser);

        if (promotePredicates.length) {
            list.sort((a, b) => {
                const aMatch = promotePredicates.some((fn) => !!fn(a));
                const bMatch = promotePredicates.some((fn) => !!fn(b));
                if (aMatch && !bMatch) return -1;
                if (!aMatch && bMatch) return 1;
                return 0; // keep existing order for ties
            });
        }

        // Default order: waitlisted first, then newly prioritized contacts, then newest message first.
        if (sortOrder === "default" && !hasSpecialPromotionFilter) {
            list.sort((a, b) => {
                const aIsWaitlist = isWaitlistContact(a);
                const bIsWaitlist = isWaitlistContact(b);
                if (aIsWaitlist && !bIsWaitlist) return -1;
                if (!aIsWaitlist && bIsWaitlist) return 1;

                const aIsPrioritized = isPrioritizedContact(a);
                const bIsPrioritized = isPrioritizedContact(b);
                if (aIsPrioritized && !bIsPrioritized) return -1;
                if (!aIsPrioritized && bIsPrioritized) return 1;

                return sortByRecentMessage(a, b);
            });
        }

        return list;
    }, [contactsForList, groupList, search, activeFilters, sortOrder, chatScreen, prioritizeUserId]);

    const hasActiveSearch = Boolean(String(searchRaw || search || "").trim());
    const hasActiveFilters = activeFilters.length > 0;
    const shouldShowContactSkeleton =
        loading || (chatScreen && !chatListLoaded && !hasActiveSearch && !hasActiveFilters);
    const activeContactId = chatScreen ? selectedContactId : null;
    const activeGroupId = chatScreen ? selectedGroupId : null;

    const emptyContactsMessage = (() => {
        if (hasActiveSearch || hasActiveFilters) {
            return "No contacts found. Try a different search or filter.";
        }
        if (chatScreen) {
            return "No chats yet. Your conversations will appear here.";
        }
        return "No contacts yet. Tap + to add your first contact.";
    })();


    // 🔹 Removed deprecated postApi wrapper - now using direct fetch calls with proper error handling

    // 🔹 Send Invite and open share modal directly with better error handling
    const handleSendInvite = useCallback(async ({ phone, countryCode, email }) => {
        if (!phone && !email) {
            toast.error("Phone or email is required");
            return;
        }

        if (!token) {
            toast.error("Session expired. Please login again.");
            return;
        }

        try {
            const fd = new FormData();
            fd.append("phone_number", phone || "");
            fd.append("country_code", countryCode || "");
            fd.append("email", email || "");
            fd.append("relations", "");

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/invite/create`, {
                method: "POST",
                body: fd,
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const data = await res.json();

            if (data?.status === "1" && data.data?.[0]?.invite_link) {
                setShareLink(data.data[0].invite_link);
                setInviteNumber(phone || "");
                setShareModal(true);
                setFriendRequestTags([]);
            } else {
                toast.error(data?.message || "Failed to create invite");
            }
        } catch (error) {
            toast.error("Something went wrong while creating invite");
        }
    }, [token]);

    // Allow parent (e.g. ProfileScreen) to trigger invite / friend-request modals externally
    useEffect(() => {
        if (typeof externalInviteTrigger !== "function") return;

        externalInviteTrigger({
            openInvite: (contact) => {
                if (!contact?.phone_number) {
                    toast.error("Mobile number required to send invitation.");
                    return;
                }
                handleSendInvite({
                    phone: contact.phone_number,
                    countryCode: contact.country_code,
                    email: contact.email,
                });
            },
            openFriendRequest: (contact) => {
                if (!contact) return;
                setFriendRequestTarget(contact);
                setFriendRequestTags([]);
                setFriendRequestModal(true);
            },
            closeInvite: () => {
                setShareModal(false);
                setFriendRequestModal(false);
                setFriendRequestTarget(null);
                setFriendRequestTags([]);
            },
        });
    }, [externalInviteTrigger, handleSendInvite]);

    // 🔹 Handle Unmove Group with better error handling
    const handleUnmoveGroup = useCallback(async (group) => {
        if (!token) {
            toast.error("Session expired. Please login again.");
            return;
        }

        const groupId = group?.group_id;
        if (!groupId) {
            toast.error("Unable to unmove this group");
            return;
        }

        try {
            setMovingGroupId(groupId);
            const body = new URLSearchParams({
                group_id: groupId,
                move_to_chat_list: "0",
            });

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/group/move_to_chat_list`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: body.toString(),
            });

            const data = await res.json();
            if (data?.status === "1") {
                toast.success(data?.message || "Group moved to archive");
                if (typeof onGroupUnmoved === "function") {
                    await Promise.resolve(onGroupUnmoved(groupId));
                }
                if (typeof onRefreshContacts === "function") {
                    await Promise.resolve(onRefreshContacts());
                }
            } else {
                toast.error(data?.message || "Failed to unmove group");
            }
        } catch (error) {
            toast.error(error?.message || "Failed to unmove group");
            } finally {
            setMovingGroupId((current) => (String(current) === String(groupId) ? null : current));
        }
    }, [token, onGroupUnmoved, onRefreshContacts]);


    // 🔹 Open Friend Request Modal
    const handleFriendRequest = useCallback((id) => {
        setFriendRequestTarget(localContacts.find(c => c.user_id === id));
        setFriendRequestTags([]);
        setFriendRequestModal(true);
    }, [localContacts]);

    // 🔹 Send Friend Request with Relations and better error handling
    const handleSendFriendRequest = useCallback(async (relations) => {
        if (!friendRequestTarget) return;

        if (!token) {
            toast.error("Session expired. Please login again.");
            return;
        }

        try {
            const fd = new FormData();
            fd.append("user_id", friendRequestTarget.user_id);
            fd.append("relations", relations.join(","));

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/friend-request/send`, {
                method: "POST",
                body: fd,
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const data = await res.json();
            toast.dismiss();

            if (data?.status === "1") {
                const responseRow = data?.data?.[0] || {};
                const nextRequestSend = Number(responseRow.request_send ?? 1);
                const relationsValue = relations.join(",");

                setLocalContacts((prev) =>
                    prev.map((contact) =>
                        String(contact.user_id) === String(friendRequestTarget.user_id)
                            ? {
                                ...contact,
                                request_send: nextRequestSend,
                                relations: responseRow.relations || relationsValue,
                                active_relations: responseRow.active_relations || relationsValue,
                            }
                            : contact
                    )
                );
                toast.success("Friend request sent!");
                dispatchContactsChanged({
                    userId: friendRequestTarget.user_id,
                    action: "friend_request_sent",
                });
                setFriendRequestModal(false);
                setFriendRequestTarget(null);
                setFriendRequestTags([]);
                if (typeof onRefreshContacts === "function") {
                    onRefreshContacts();
                }
            } else {
                toast.error(data?.message || "Failed to send friend request");
            }
        } catch (error) {
            toast.dismiss();
            toast.error("Something went wrong while sending request");
        }
    }, [friendRequestTarget, token, onRefreshContacts]);

    // 🔹 Accept Friend Request with better error handling
    const handleAcceptRequest = useCallback(async () => {
        if (!selectedUser || !token) {
            toast.error("Session expired or invalid user");
            return;
        }

        try {
            const fd = new FormData();
            fd.append("user_id", selectedUser.user_id);
            fd.append("action", 1);
            fd.append("tag", tagModalTags.join(","));

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/friend-request/accept_reject_friend_request`, {
                method: "POST",
                body: fd,
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const data = await res.json();
            if (data?.status === "1") {
                toast.success("Friend request accepted!");
                setTagModal(false);
                setTagModalTags([]);
            } else {
                toast.error(data?.message || "Failed to accept request");
            }
        } catch (error) {
            toast.error("Something went wrong while accepting request");
        }
    }, [selectedUser, token, tagModalTags]);
    // If loading, render skeleton UI immediately (hooks have already been declared)
    if (loading) {
        return (
            <div
                className="h-full min-h-0 w-full overflow-hidden bg-white"
            >
                <aside className="flex h-full min-h-0 flex-col bg-white p-4 pt-0 pr-0 mt-4 md:w-[350px] md:min-w-[300px] md:flex-shrink-0">
                    <div className="relative w-full mb-6">
                        <div className="h-12 w-full rounded-[20px] bg-gray-200 animate-pulse" />
                    </div>

                    <div className="pb-2 flex justify-between overflow-x-auto px-2">
                        {Array.from({ length: 4 }).map((_, idx) => (
                            <div
                                key={idx}
                                className="h-8 w-20 rounded-full bg-gray-200 animate-pulse mr-2"
                            />
                        ))}
                    </div>

                    <div className="min-h-0 flex-1 space-y-3 mt-4 overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]">
                        {Array.from({ length: 9 }).map((_, idx) => (
                            <div
                                key={idx}
                                className="flex items-center gap-3 p-3 py-1 rounded-[100px] bg-gray-200 bg-opacity-60 animate-pulse w-full"
                            >
                                <div className="h-12 w-12 rounded-full bg-gray-300" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 w-32 bg-gray-300 rounded" />
                                    <div className="h-3 w-24 bg-gray-300 rounded" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded-full bg-gray-300" />
                                    <div className="h-8 w-8 rounded-full bg-gray-300" />
                                </div>
                            </div>
                        ))}
                    </div>
                </aside>
            </div>
        );
    }

    return (
        <div
            className="h-full min-h-0 w-full overflow-hidden bg-white"
        >
            <aside className="flex h-full min-h-0 flex-col bg-white p-4 pt-0 md:pr-0 mt-4 md:w-[350px] md:min-w-[300px] md:flex-shrink-0">
                <div className="flex gap-2 w-full items-center mb-6">
                    <div className="relative flex-1">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                            <img
                                src="../chatbox/magnifer.svg"
                                alt="magnifer"
                                className=" w-5 h-5"
                            />
                        </div>
                        <input
                            id="search"
                            type="search"
                            value={searchRaw}
                            onChange={(e) => setSearchRaw(e.target.value)}
                            placeholder="Search"
                            className="w-full rounded-[20px] border border-gray-400 bg-white pl-12 pr-12 py-3 text-gray-800 placeholder:text-gray-400 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-md shadow-[#7C4DFF] sticky top-4 [appearance:textfield] [&::-webkit-search-cancel-button]:hidden"
                        />
                        {searchRaw && (
                            <button
                                type="button"
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => setSearchRaw("")}
                                aria-label="Clear search"
                            >
                                <img src="../chatbox/close.svg" alt="close" className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                    {profileData?.refreshstatus === 1 && (
                        <button
                            onClick={handleRefreshClick}
                            disabled={refreshing}
                            className="disabled:opacity-50 transition-opacity submit-button !p-2 !rounded-xl !h-max"
                            title="Refresh status"
                        >
                            <img
                                src="../refresh.svg"
                                className={`h-6 w-6 ${refreshing ? 'animate-spin' : ''}`}
                                alt="refresh"
                            />
                        </button>
                    )}
                    {!chatScreen && (
                        <button
                            type="button"
                            onClick={handleOpenAddContactModal}
                            className="h-[28px] w-[28px] px-3 rounded-lg bg-[#6938EF] text-white flex items-center justify-center shadow-md hover:bg-[#5B30CC] transition"
                            title="Add contact"
                            aria-label="Add contact"
                        >
                            <span className="text-2xl leading-none">+</span>
                        </button>
                    )}
                </div>

                {showAddContactModal && (
                    <div
                        className="fixed inset-0 z-[21000] isolate bg-black bg-opacity-50 flex items-center justify-center p-4"
                        onClick={handleCloseAddContactModal}
                    >
                        <div
                            className="relative z-[21010] w-full max-w-[560px] bg-white rounded-3xl p-6 sm:p-8 shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="text-2xl font-semibold text-gray-900">Add contact</h3>
                                <button
                                    type="button"
                                    onClick={handleCloseAddContactModal}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <img src="/input-logos/close.svg" alt="close" className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-600">*</span></label>
                                    <input
                                        type="text"
                                        value={contactForm.firstName}
                                        onChange={(e) => setContactForm((prev) => ({ ...prev, firstName: e.target.value.replace(/[^A-Za-z\s\-']/g, '') }))}
                                        className="w-full rounded-2xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500"
                                        placeholder="Enter first name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-600">*</span></label>
                                    <input
                                        type="text"
                                        value={contactForm.lastName}
                                        onChange={(e) => setContactForm((prev) => ({ ...prev, lastName: e.target.value.replace(/[^A-Za-z\s\-']/g, '') }))}
                                        className="w-full rounded-2xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500"
                                        placeholder="Enter last name"
                                    />
                                </div>
                            </div>

                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-600">*</span></label>
                                <div
                                    className={`flex rounded-2xl border overflow-hidden focus-within:ring-2 ${contactFormTouched.phone && phoneError
                                        ? "border-red-500 focus-within:ring-red-200"
                                        : "border-gray-300 focus-within:ring-violet-500"
                                        }`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setShowCountryModal(true)}
                                        className="px-3 min-w-[86px] border-r border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100"
                                    >
                                        {contactForm.countryCode}
                                    </button>
                                    <input
                                        type="tel"
                                        value={contactForm.phoneNumber}
                                        onChange={(e) => setContactForm((prev) => ({
                                            ...prev,
                                            phoneNumber: e.target.value
                                                .replace(/\D/g, "")
                                                .slice(0, ((prev.countryCode || "").trim() === "+91" ? 10 : 15)),
                                        }))}
                                        onBlur={() => setContactFormTouched((prev) => ({ ...prev, phone: true }))}
                                        className="w-full px-4 py-3 focus:outline-none"
                                        placeholder="Enter phone number"
                                    />
                                </div>
                                {contactFormTouched.phone && phoneError && (
                                    <p className="mt-1 text-xs text-red-500">{phoneError}</p>
                                )}
                            </div>

                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                                <input
                                    type="email"
                                    value={contactForm.email}
                                    onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
                                    className="w-full rounded-2xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500"
                                    placeholder="Enter email"
                                />
                            </div>

                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={handleCloseAddContactModal}
                                    className="rounded-2xl border border-violet-500 text-violet-600 py-3 font-semibold hover:bg-violet-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmitContact}
                                    disabled={disableSubmitContact}
                                    className="rounded-2xl bg-violet-600 text-white py-3 font-semibold hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {submittingContact ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showCountryModal && (
                    <CountryModel
                        onSelect={handleCountrySelect}
                        onClose={() => setShowCountryModal(false)}
                    />
                )}

                {/* 🔥 Confirm Modal */}
                {confirmModal && selectedUser && (
                    <ModalWrapper onClose={() => setConfirmModal(false)}>
                        <p className="mb-5 text-lg font-semibold">
                            Accept {selectedUser.full_name}'s request?
                        </p>

                        <button
                            className="w-full px-[30px] py-[12px] border border-purple-600 rounded-[25px] mb-3"
                            onClick={() => setConfirmModal(false)}
                        >
                            Decline
                        </button>

                        <button
                            className="w-full submit-button"
                            onClick={() => {
                                setConfirmModal(false);
                                setTagModal(true);
                            }}
                        >
                            Accept
                        </button>
                    </ModalWrapper>
                )}
                {shareModal && (
                    <ShareModal
                        link={shareLink}
                        phoneNumber={inviteNumber}
                        onClose={() => setShareModal(false)}
                    />
                )}

                {showImageModal && (
                    <ModalWrapper onClose={() => setShowImageModal(false)}>
                        <div className="flex flex-col items-center gap-4">
                            <div className="h-64 w-64 rounded-full overflow-hidden border-4 border-gray-200 flex items-center justify-center bg-gray-100">
                                <img
                                    src={imagePreview || "../chatbox/default.png"}
                                    onError={(e) => {
                                        e.target.src = "../chatbox/default.png";
                                    }}
                                    alt="Profile"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <button
                                className="w-full submit-button"
                                onClick={() => setShowImageModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </ModalWrapper>
                )}

                {/* 🔹 Group Image Preview Modal */}
                {showGroupImageModal && (
                    <ModalWrapper onClose={() => setShowGroupImageModal(false)}>
                        <div className="flex flex-col items-center gap-4">
                            <div className="h-64 w-64 rounded-full overflow-hidden border-4 border-gray-200 flex items-center justify-center bg-gray-100">
                                <img
                                    src={groupImagePreview}
                                    alt="Group"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <button
                                className="w-full submit-button"
                                onClick={() => setShowGroupImageModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </ModalWrapper>
                )}

                {/* 🔥 Friend Request Modal */}
                {friendRequestModal && friendRequestTarget && (
                    <ModalWrapper onClose={() => {
                        setFriendRequestModal(false);
                        setFriendRequestTarget(null);
                        setFriendRequestTags([]);
                    }}>
                        <p className="text-lg font-semibold mb-4">
                            Please select how do you know {friendRequestTarget.full_name || friendRequestTarget.name}
                        </p>
                        <div className="gap-3 flex flex-col">
                            {inviteFilters.map((tag, index) => {
                                const isSelected = friendRequestTags.includes(tag);
                                return (
                                    <div
                                        key={index}
                                        onClick={() => toggleTagSelection(tag, setFriendRequestTags)}
                                        className={`flex items-center justify-between p-3 rounded-[100px] border shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg gap-3
                ${isSelected
                                                ? "bg-[#EDE7FF] border-[#7C4DFF]"
                                                : "bg-[#797C7B] bg-opacity-[11%] border-gray-200"
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <p className="font-semibold text-sm text-gray-900">
                                                {tag}
                                            </p>
                                        </div>

                                        <div
                                            className={`w-6 h-6 flex items-center justify-center rounded-full border-2 transition-all duration-200
                    ${isSelected
                                                    ? "bg-[#7C4DFF] border-[#7C4DFF]"
                                                    : "border-gray-400"
                                                }`}
                                        >
                                            {isSelected && (
                                                <img
                                                    src="../chatscreen/radiobutton.svg"
                                                    alt="Selected"
                                                    className="w-6 h-6"
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <button
                            className="w-full submit-button mt-4"
                            onClick={() => handleSendFriendRequest(friendRequestTags)}
                        >
                            Send Request
                        </button>
                        <button
                            className="w-full normal-button mt-2"
                            onClick={() => {
                                setFriendRequestModal(false);
                                setFriendRequestTarget(null);
                                setFriendRequestTags([]);
                            }}
                        >
                            Cancel
                        </button>
                    </ModalWrapper>
                )}

                {/* 🔥 Tag Modal */}
                {tagModal && selectedUser && (
                    <ModalWrapper onClose={() => {
                        setTagModal(false);
                        setTagModalTags([]);
                    }}>
                        <p className="text-lg font-semibold mb-4">
                            Please select how do you know {selectedUser.full_name || selectedUser.name}
                        </p>
                        <div className="gap-3 flex flex-col">
                            {filters.map((tag, index) => {
                                const isSelected = tagModalTags.includes(tag);
                                return (
                                    <div
                                        key={index}
                                        onClick={() => toggleTagSelection(tag, setTagModalTags)}
                                        className={`flex items-center justify-between p-3 rounded-[100px] border shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg gap-3
                ${isSelected
                                                ? "bg-[#EDE7FF] border-[#7C4DFF]"
                                                : "bg-[#797C7B] bg-opacity-[11%] border-gray-200"
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <p className="font-semibold text-sm text-gray-900">
                                                {tag}
                                            </p>
                                        </div>

                                        <div
                                            className={`w-6 h-6 flex items-center justify-center rounded-full border-2 transition-all duration-200
                    ${isSelected
                                                    ? "bg-[#7C4DFF] border-[#7C4DFF]"
                                                    : "border-gray-400"
                                                }`}
                                        >
                                            {isSelected && (
                                                <img
                                                    src="../chatscreen/radiobutton.svg"
                                                    alt="Selected"
                                                    className="w-6 h-6"
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <button
                            className="w-full submit-button mt-3"
                            onClick={handleAcceptRequest}
                        >
                            Submit
                        </button>

                        <button
                            className="w-full normal-button mt-3"
                            onClick={() => {
                                setTagModal(false);
                                setTagModalTags([]);
                            }}
                        >
                            Cancel
                        </button>
                    </ModalWrapper>
                )}

                {/* 🔥 Birthdays */}
                {bdayData.length > 0 && (
                    <div className="">
                        <div className="flex gap-5 overflow-x-auto scrollbar-thin">
                            {bdayData.map((c, idx) => (
                                <button
                                    key={c?.user_id || c?.original?.user_id || `${c?.name || "birthday"}-${idx}`}
                                    type="button"
                                    onClick={() => handleBirthdayContactOpen(c)}
                                    className="flex flex-col items-center min-w-[70px] cursor-pointer"
                                >
                                    <div
                                        className={`relative h-14 w-14 rounded-full p-[2px] ${c.type === "anniversary"
                                            ? "bg-gradient-to-br from-amber-400 to-yellow-200"
                                            : "bg-gradient-to-br from-[#7C4DFF] to-[#9D7FFF]"
                                            }`}
                                    >
                                        <div className="h-full w-full rounded-full bg-white p-[2px]">
                                            <img
                                                src={c.avatar}
                                                alt="profile"
                                                className="h-full w-full rounded-full object-cover"
                                            />
                                        </div>

                                        <div
                                            className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${c.type === "anniversary"
                                                ? "bg-amber-500"
                                                : "bg-[#7C4DFF]"
                                                }`}
                                        >
                                            {c.type === "anniversary" ? (
                                                <img src="../ic_ring.svg" alt="anniversary" className="h-3 w-3" />
                                            ) : (
                                                <img src="../ic_birthday.svg" alt="birthday" className="h-3 w-3" />
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-[12px] text-center w-16 mt-1 text-ellipsis overflow-hidden whitespace-nowrap">{c.name}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* 🔥 Filters */}
                {filterSaw && (
                    <div className="pb-2 flex justify-between  px-2 gap-1 items-center overflow-visible">
                        <div className="flex justify-between gap-2 flex-1 pr-2">
                            {filters.map((filter) => (
                                <button
                                    key={filter}
                                    onClick={() => handleFilterToggle(filter)}
                                    className={`rounded-full text-sm px-3 py-1 mb-2 font-medium transition-all duration-200 whitespace-nowrap
                                    ${activeFilters.includes(filter)
                                            ? "bg-[#6938EF] text-white  font-semibold shadow-[0px_4px_4px_rgba(105,56,239,0.37)]"
                                            : "text-[#111] font-semibold bg-white shadow-[0px_4px_4px_rgba(105,56,239,0.37)]"
                                        }`}
                                >
                                    {filter}
                                </button>
                            ))}
                        </div>

                        {/* Sort Icon Button */}
                        <div className="relative">
                            <button
                                onClick={() => setShowSortDropdown(!showSortDropdown)}
                                className={`p-1 w-6 mt-1 rounded-full transition-all duration-200 flex items-center justify-center mb-2 ${showSortDropdown
                                    ? "bg-[#6938EF]"
                                    : ""
                                    }`}
                                title="Sort contacts"
                            >
                                <svg
                                    className={`w-5 h-5 transition-colors ${showSortDropdown ? "text-white" : "text-[#6938EF]"}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                    />
                                </svg>
                            </button>

                            {/* Dropdown Menu */}
                            {showSortDropdown && (
                                <div ref={sortDropdownRef} className="absolute top-full right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 z-[999] overflow-hidden">
                                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Sort By</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setSortOrder("default");
                                            setActiveFilters(prev => prev.filter(f => !UI_SPECIAL_FILTERS.includes(f)));
                                            setShowSortDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center gap-3 ${sortOrder === "default" && !activeFilters.some(f => UI_SPECIAL_FILTERS.includes(f))
                                            ? "bg-[#EDE7FF] text-[#6938EF]"
                                            : "text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5.951-1.429 5.951 1.429a1 1 0 001.169-1.409l-7-14z" />
                                        </svg>
                                        Default
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortOrder("a-z");
                                            setActiveFilters(prev => prev.filter(f => !UI_SPECIAL_FILTERS.includes(f)));
                                            setShowSortDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center gap-3 ${sortOrder === "a-z"
                                            ? "bg-[#EDE7FF] text-[#6938EF]"
                                            : "text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h9a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z" />
                                        </svg>
                                        A - Z
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortOrder("z-a");
                                            setActiveFilters(prev => prev.filter(f => !UI_SPECIAL_FILTERS.includes(f)));
                                            setShowSortDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center gap-3 ${sortOrder === "z-a"
                                            ? "bg-[#EDE7FF] text-[#6938EF]"
                                            : "text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h7a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h4a1 1 0 110 2H4a1 1 0 01-1-1z" />
                                        </svg>
                                        Z - A
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSortOrder("oldest");
                                            setActiveFilters(prev => prev.filter(f => !UI_SPECIAL_FILTERS.includes(f)));
                                            setShowSortDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center gap-3 ${sortOrder === "oldest"
                                            ? "bg-[#EDE7FF] text-[#6938EF]"
                                            : "text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                        </svg>
                                        Oldest Messages First
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleUnreadToggle();
                                            setShowSortDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center gap-3 ${sortOrder === "unread"
                                            ? "bg-[#EDE7FF] text-[#6938EF]"
                                            : "text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
                                       <img src="../chatbox/phonebook.svg" alt="Unread" className="w-3.5 h-3.5" />
                                        Unread Messages First
                                    </button>

                                    <button
                                        onClick={() => {
                                            handleFilterToggle("Unadded phonebookplus contacts")
                                            setSortOrder("default");
                                            setShowSortDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center gap-3 ${activeFilters.includes("Unadded phonebookplus contacts")
                                            ? "bg-[#EDE7FF] text-[#6938EF]"
                                            : "text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
  <img src="../chatbox/add-friend.svg" alt="Uninvited" className="w-3.5 h-3.5" />                                        Unadded phonebookplus contacts
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleFilterToggle("Uninvited contacts")
                                            setSortOrder("default");
                                            setShowSortDropdown(false);

                                        }}
                                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-all duration-200 flex items-center gap-3 ${activeFilters.includes("Uninvited contacts")
                                            ? "bg-[#EDE7FF] text-[#6938EF]"
                                            : "text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
                                                                             <img src="../chatbox/invite.svg" alt="Unadded" className="w-3.5 h-3.5" />
                                                                           
                                        Uninvited contacts
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}


                {/* 🔥 Contact List */}
                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]">
                    {shouldShowContactSkeleton ? (
                        <div>
                            {Array.from({ length: 9 }).map((_, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-center gap-3 p-3 py-1 my-2 rounded-[100px] bg-gray-200 bg-opacity-60 animate-pulse w-full"
                                >
                                    <div className="h-12 w-12 rounded-full bg-gray-300" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 w-32 bg-gray-300 rounded" />
                                        <div className="h-3 w-24 bg-gray-300 rounded" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="h-8 w-8 rounded-full bg-gray-300" />
                                        <div className="h-8 w-8 rounded-full bg-gray-300" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : filteredContacts.length === 0 ? (
                        <div className="h-[70vh] flex flex-col items-center justify-center">
                            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
                                <img src="../logo.svg" alt=" PhoneBookPlus Logo" />
                            </div>
                            <h1 className="text-2xl font-semibold tracking-tight text-gray-500 font-inter text-center">
                                 PhoneBookPlus
                            </h1>
                            <p className="text-center text-gray-500 flex items-center justify-center px-4">
                                {emptyContactsMessage}
                            </p>
                        </div>
                    ) : (
                        filteredContacts.map((c) => {
                            // 🔹 Check if this is a group
                            const isGroup = c?.group_id || c?.group_name;
                            const isUnread = isGroup ? Number(c.group_message_read) === 1 : false;
                            const isActiveContact = activeContactId && String(c.user_id) === String(activeContactId);
                            const isActiveGroup = activeGroupId && String(c.group_id) === String(activeGroupId);

                            if (isGroup) {
                                return (
                                    <div
                                        key={`${c.group_id}-${c.group_message_read}`}
                                        role="button"
                                        tabIndex={0}
                                        className={`flex items-center gap-3 p-3 py-1 rounded-[100px] border shadow-xl w-full z-40 ${isActiveGroup
                                            ? "bg-[#EDE7FF] border-[#7C4DFF]"
                                            : "bg-[#797C7B] border-gray-100 bg-opacity-[11%]"
                                            }`}
                                        onClick={() => {
                                            // Only open group details if a handler is provided (in-chat behavior).
                                            // Avoid redirecting the whole row — keep redirect mapped to the info button.
                                            if (typeof onSelectGroupDetails === 'function') {
                                                onSelectGroupDetails(c.group_id);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                if (onSelectGroupDetails) {
                                                    onSelectGroupDetails(c.group_id);
                                                }
                                            }
                                        }}
                                    >
                                        <div
                                            className="h-12 w-12 rounded-full overflow-hidden flex-shrink-0 bg-gray-200 flex items-center justify-center cursor-pointer hover:opacity-80 transition"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setGroupImagePreview(`${process.env.REACT_APP_API_BASE_URL}${c.group_image_url}`);
                                                setShowGroupImageModal(true);
                                            }}
                                        >
                                            <img
                                                src={`${process.env.REACT_APP_API_BASE_URL}${c.group_image_url}`}
                                                alt={c.group_name}
                                                className="h-full w-full object-cover"
                                            />
                                        </div>

                                        <div className="flex justify-between items-center w-full">
                                            <p className="text-lg font-semibold">{c.group_name}</p>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    className="rounded-full text-xs font-semibold text-white disabled:opacity-60"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUnmoveGroup(c);
                                                    }}
                                                    disabled={String(movingGroupId) === String(c.group_id)}
                                                    title="Move to group"
                                                >
                                                    {/* {String(movingGroupId) === String(c.group_id) ? "Moving..." : "Unmove"} */}
                                                    <img
                                                        src="../move.svg"
                                                        className="w-5 h-5"
                                                        alt="status"
                                                    />
                                                </button>

                                                <img
                                                    src={isUnread ? "../groupscreen/phonebook.svg" : "../chatbox/phonebook.svg"}
                                                    className="w-5 h-5 cursor-pointer hover:opacity-80 transition"
                                                    alt="open group chat"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Phonebook icon: prefer opening the group chat (navigate to chat)
                                                        if (typeof redirectToChat === 'function') {
                                                            redirectToChat?.(c.group_id);
                                                            return;
                                                        }
                                                        // Fallback to opening group details if navigation isn't available
                                                        if (typeof onSelectGroupDetails === 'function') {
                                                            onSelectGroupDetails(c.group_id);
                                                        }
                                                    }}
                                                />

                                                <img
                                                    src="../groupscreen/info.svg"
                                                    className="w-5 h-5 cursor-pointer hover:opacity-80 transition"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Info icon: open group info/details panel when possible
                                                        if (typeof onGroupInfoClick === 'function') {
                                                            onGroupInfoClick(c.group_id);
                                                            return;
                                                        }
                                                        if (typeof onSelectGroupDetails === 'function') {
                                                            onSelectGroupDetails(c.group_id);
                                                            return;
                                                        }
                                                        // Last-resort: navigate to chat if no details handler exists
                                                        if (typeof redirectToChat === 'function') {
                                                            redirectToChat?.(c.group_id);
                                                        }
                                                    }}
                                                    title="Open group info"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={c.user_id}
                                    className={`flex items-center gap-3 p-3 py-1 rounded-[100px] border shadow-xl w-full z-40 ${isActiveContact
                                        ? "bg-[#EDE7FF] border-[#7C4DFF]"
                                        : "bg-[#797C7B] border-gray-100 bg-opacity-[11%]"
                                        }`}
                                >
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => {
                                            openContactFromList(c);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                openContactFromList(c);
                                            }
                                        }}
                                        className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                                    >
                                        <div className="relative">
                                            <img
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const imgSrc = (c.profile_image || c.profile_image_url)
                                                        ? `${process.env.REACT_APP_API_BASE_URL}${c.profile_image || c.profile_image_url}`
                                                        : "../chatbox/default.png";
                                                    setImagePreview(imgSrc);
                                                    setShowImageModal(true);
                                                }}
                                                src={
                                                    (c.profile_image || c.profile_image_url)
                                                        ? `${process.env.REACT_APP_API_BASE_URL}${c.profile_image || c.profile_image_url}`
                                                        : "/profile-picture.png"
                                                }
                                                onError={(e) => {
                                                    e.target.src = "/profile-picture.png";
                                                }}
                                                className="h-12 w-12 rounded-full object-cover cursor-pointer"
                                                alt="profile"
                                            />

                                            {isWaitlisted(c) && (
                                                <span
                                                    title="Waiting list"
                                                    className="absolute -left-0 -top-1 h-4 w-4 rounded-full bg-[#6938EF] border-2 border-white"
                                                />
                                            )}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <p
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (shouldOpenFriendProfile(c)) {
                                                        openFriendProfile(c.user_id);
                                                    } else {
                                                        openContactFromList(c);
                                                    }
                                                }}
                                                className={`heading-font text-left ${chatScreen ? "w-max max-w-[102%] line-clamp-1" : "max-w-[102%] line-clamp-1"} cursor-pointer`}
                                            >
                                                {c.nick_name || c.full_name || c.name || "--"}
                                            </p>
                                            {isWaitlisted(c) && (
                                                <p className="text-xs text-[#6938EF]">Waitlisted: Send an invitation</p>
                                            )}
                                            {(() => {
                                                const statusText = parseContactStatusText(c?.status_text).trim();
                                                const hasStatusText = statusText.length > 0;
                                                const statusData = c?.status_data;
                                                const offlineStatus = Array.isArray(statusData)
                                                    ? statusData.find((s) => s.offlineuntil === 1 && !s.is_expired)
                                                    : null;
                                                const displayText = hasStatusText
                                                    ? statusText
                                                    : formatOfflineStatusMessage(offlineStatus).trim();

                                                if (!displayText) return null;

                                                const isOfflineMarquee = !hasStatusText && !!offlineStatus;
                                                const isLongStatus = displayText.length > 24;
                                                const isScrollable = isOfflineMarquee || isLongStatus;
                                                const userIdKey = String(c.user_id);
                                                const playAutoOnce =
                                                    isOfflineMarquee &&
                                                    !statusAutoScrollDone[userIdKey];

                                                return (
                                                    <div
                                                        className={`status-marquee status-marquee--contact mt-1 min-h-0 w-full max-w-full ${isScrollable ? "touch-pan-x" : "status-marquee--static"}`}
                                                        onWheel={isScrollable ? (e) => handleStatusWheel(e, c.user_id) : undefined}
                                                        onTouchStart={isScrollable ? () => markStatusAutoDone(c.user_id) : undefined}
                                                        onMouseDown={isScrollable ? () => markStatusAutoDone(c.user_id) : undefined}
                                                    >
                                                        <span
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (shouldOpenFriendProfile(c)) {
                                                                    openFriendProfile(c.user_id);
                                                                } else {
                                                                    openContactFromList(c);
                                                                }
                                                            }}
                                                            onAnimationEnd={playAutoOnce ? () => markStatusAutoDone(c.user_id) : undefined}
                                                            className={`status-marquee__text status-marquee__text--contact body-font block max-w-none text-left leading-snug ${playAutoOnce ? "status-marquee__text--auto-once max-md:animate-none" : ""
                                                                } cursor-pointer`}
                                                        >
                                                            {displayText}
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                        {isFriendContact(c.is_friend) && (
                                            <button
                                                className="place-items-center"
                                                onClick={() => window.open(`mailto:${c.email}`)}
                                                title="Message"
                                            >
                                                <img src="../chatbox/message.svg" alt="message" className="w-5 h-5" />
                                            </button>
                                        )}

                                        {!isWaitlisted(c) &&
                                        !isFriendContact(c.is_friend) &&
                                        (Number(c.pending_relation_selection || 0) === 1 ||
                                            Number(c.invite_link_case || 0) === 2) ? (
                                            <button
                                                className="rounded-full cursor-not-allowed"
                                                disabled
                                                title="Relation selection pending"
                                            >
                                                <img alt="add friend" src="../chatbar/check.svg" className="w-5 h-5" />
                                            </button>
                                        ) : c.phone_book_user === 0 ? (
                                            <button
                                                className="grid place-items-center"
                                                onClick={() => handleSendInvite({
                                                    phone: c.phone_number,
                                                    countryCode: c.country_code,
                                                    email: c.email
                                                })}
                                                title="Invite user"
                                            >
                                                <img src="../chatbox/invite.svg" alt="invite" className="w-5 h-5" />
                                            </button>
                                        ) : isFriendContact(c.is_friend) ? (
                                            <button
                                                className="grid place-items-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    redirectToChat?.(c.user_id, getContactPhone(c));
                                                }}
                                                title="Phonebook"
                                            >
                                                <img
                                                    src={
                                                        hasUnreadMessages(c, activeContactId)
                                                            ? "../groupscreen/phonebook.svg"
                                                            : "../chatbox/phonebook.svg"
                                                    }
                                                    alt="phonebook"
                                                    className="w-5 h-5"
                                                />
                                            </button>
                                        ) : (
                                            (() => {
                                                const isFriend = isFriendContact(c.is_friend);
                                                if (!isFriend && c.phone_book_user !== 0) {
                                                    const requestSendValue = Number(c.request_send || 0);
                                                    const isRequestSent = requestSendValue === 1 || requestSendValue === 2;
                                                    return (
                                                        <button
                                                            onClick={() => handleFriendRequest(c.user_id)}
                                                            className="grid place-items-center"
                                                            title="Add friend"
                                                            disabled={isRequestSent}
                                                        >
                                                            <img src={isRequestSent ? "../chatbar/check.svg" : "../chatbox/add-friend.svg"} alt="add friend" className="w-5 h-5" />
                                                        </button>
                                                    );
                                                }
                                                return null;
                                            })()
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </aside>
        </div>
    );
}
